/** Admin API helpers — requires Supabase Auth session for Alain only. */

const API_BASE =
  (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") ||
  (typeof window !== "undefined" ? "" : "http://127.0.0.1:8000");

function apiUrl(path: string): string {
  if (API_BASE) return `${API_BASE}${path}`;
  return path;
}

const TOKEN_KEY = "pitchai_admin_access_token";
const REFRESH_KEY = "pitchai_admin_refresh_token";

export interface PublicConfig {
  supabase_url: string | null;
  supabase_anon_key: string | null;
  public_app_url?: string | null;
}

export interface AdminPresentationRow {
  id: string;
  slug: string;
  owner_name: string;
  property: string;
  status?: string;
  created_at?: string | null;
  published_at?: string | null;
  presentation_path?: string | null;
  opened: boolean;
  views: number;
  first_opened_at?: string | null;
  last_opened_at?: string | null;
  cta_labels: string[];
  unique_sessions: number;
}

export interface AdminActivityItem {
  id?: string;
  event_type: string;
  label: string;
  created_at?: string | null;
  session_id?: string | null;
  device?: string;
  referrer?: string;
}

export interface AdminPresentationDetail extends AdminPresentationRow {
  presentation_url?: string | null;
  activity: AdminActivityItem[];
}

export function getStoredAccessToken(): string | null {
  try {
    // localStorage so admin exclusion works across tabs (Open Presentation → /p/:slug).
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearAdminSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
  } catch {
    // ignore
  }
}

function storeSession(accessToken: string, refreshToken?: string | null): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  // Clear legacy sessionStorage copies if present.
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
  } catch {
    // ignore
  }
}

let cachedPublicAppUrl: string | null | undefined;

export async function getPublicAppUrl(): Promise<string> {
  if (cachedPublicAppUrl !== undefined && cachedPublicAppUrl !== null) {
    return cachedPublicAppUrl;
  }
  try {
    const cfg = await fetchPublicConfig();
    cachedPublicAppUrl = (cfg.public_app_url || "").replace(/\/$/, "") || "";
  } catch {
    cachedPublicAppUrl = "";
  }
  return cachedPublicAppUrl || "";
}

/** Admin Open Presentation — canonical domain + preview=1 (analytics excluded when Alain session present). */
export function adminPreviewPath(slug: string, publicAppUrl?: string | null): string {
  const path = `/p/${encodeURIComponent(slug)}?preview=1`;
  const base = (publicAppUrl || cachedPublicAppUrl || "").replace(/\/$/, "");
  return base ? `${base}${path}` : path;
}

/** Clean customer-facing presentation URL (no preview flag). */
export function publicPresentationUrl(slug: string, publicAppUrl?: string | null): string {
  const path = `/p/${encodeURIComponent(slug)}`;
  const base = (publicAppUrl || cachedPublicAppUrl || "").replace(/\/$/, "");
  return base ? `${base}${path}` : path;
}

/** Canonical PDF download URL for a presentation slug. */
export function publicPresentationPdfUrl(
  slug: string,
  publicAppUrl?: string | null
): string {
  const path = `/p/${encodeURIComponent(slug)}/pdf`;
  const base = (publicAppUrl || cachedPublicAppUrl || "").replace(/\/$/, "");
  return base ? `${base}${path}` : path;
}

export async function resetAdminPresentationEvents(
  presentationId: string
): Promise<{ ok: boolean; deleted: number }> {
  const token = getStoredAccessToken();
  if (!token) throw new Error("Sign in required");
  const res = await fetch(
    apiUrl(`/api/admin/presentations/${encodeURIComponent(presentationId)}/events`),
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (res.status === 401 || res.status === 403) {
    clearAdminSession();
    throw new Error("Sign in required");
  }
  if (!res.ok) throw new Error("Could not reset analytics");
  return (await res.json()) as { ok: boolean; deleted: number };
}

export async function fetchPublicConfig(): Promise<PublicConfig> {
  const res = await fetch(apiUrl("/api/public/config"));
  if (!res.ok) throw new Error("Could not load auth config");
  return (await res.json()) as PublicConfig;
}

export async function adminSignIn(
  email: string,
  password: string
): Promise<{ email: string }> {
  const config = await fetchPublicConfig();
  if (!config.supabase_url || !config.supabase_anon_key) {
    throw new Error("Supabase Auth is not configured");
  }
  const res = await fetch(
    `${config.supabase_url}/auth/v1/token?grant_type=password`,
    {
      method: "POST",
      headers: {
        apikey: config.supabase_anon_key,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email: email.trim(), password }),
    }
  );
  if (!res.ok) {
    throw new Error("Invalid email or password");
  }
  const data = (await res.json()) as {
    access_token?: string;
    refresh_token?: string;
    user?: { email?: string };
  };
  if (!data.access_token) throw new Error("Invalid email or password");
  storeSession(data.access_token, data.refresh_token);
  // Confirm allowlist via our admin API (blocks non-Alain accounts).
  const me = await adminFetchMe();
  return me;
}

export async function adminFetchMe(): Promise<{ email: string; id: string }> {
  const token = getStoredAccessToken();
  if (!token) throw new Error("Sign in required");
  const res = await fetch(apiUrl("/api/admin/me"), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401 || res.status === 403) {
    clearAdminSession();
    throw new Error(
      res.status === 403
        ? "This account is not allowed to access admin"
        : "Sign in required"
    );
  }
  if (!res.ok) throw new Error("Could not verify session");
  return (await res.json()) as { email: string; id: string };
}

async function authedGet<T>(path: string): Promise<T> {
  const token = getStoredAccessToken();
  if (!token) throw new Error("Sign in required");
  const res = await fetch(apiUrl(path), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401 || res.status === 403) {
    clearAdminSession();
    throw new Error(
      res.status === 403
        ? "This account is not allowed to access admin"
        : "Sign in required"
    );
  }
  if (!res.ok) throw new Error("Request failed");
  return (await res.json()) as T;
}

export async function fetchAdminPresentations(): Promise<AdminPresentationRow[]> {
  const data = await authedGet<{ presentations: AdminPresentationRow[] }>(
    "/api/admin/presentations"
  );
  return data.presentations || [];
}

export async function fetchAdminPresentationDetail(
  id: string
): Promise<AdminPresentationDetail> {
  return authedGet<AdminPresentationDetail>(
    `/api/admin/presentations/${encodeURIComponent(id)}`
  );
}

export interface AdminCreatePresentationResult {
  id: string;
  status: string;
  slug?: string | null;
  url?: string | null;
  pdf_url?: string | null;
  error?: { code?: string; message?: string } | null;
}

/** Manual /create — admin session only; reuses production generation engine. */
export async function createAdminPresentation(input: {
  streeteasy_url: string;
  owner_name?: string | null;
}): Promise<AdminCreatePresentationResult> {
  const token = getStoredAccessToken();
  if (!token) throw new Error("Sign in required");
  const res = await fetch(apiUrl("/api/admin/presentations"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      streeteasy_url: input.streeteasy_url,
      owner_name: input.owner_name || null,
    }),
  });
  if (res.status === 401 || res.status === 403) {
    clearAdminSession();
    throw new Error("Sign in required");
  }
  const data = (await res.json().catch(() => ({}))) as AdminCreatePresentationResult & {
    detail?: { code?: string; message?: string } | string;
  };
  if (!res.ok) {
    const detail = data.detail;
    if (typeof detail === "object" && detail?.message) {
      throw new Error(detail.message);
    }
    if (typeof detail === "string") throw new Error(detail);
    throw new Error("Couldn’t generate this presentation. Please try again.");
  }
  return data;
}

export function formatAdminTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}

export function formatAdminTimeShort(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}
